import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { useStyles } from "./CorporationDetails.styles";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Button, IconButton } from "@mui/material";
import { MainTable } from "components";
import { useMemo, useState } from "react";
import { MillForm } from "../../MillForm/MillForm.view";
import { useTranslation } from "react-i18next";
import { ICorporateDetailRow } from "./CorporationDetailsInterfaces";
import { useCorporationsDetailsColumns } from "./hooks/useCorporationsDetailsColumns";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { MADERA_BACKOFFICE_PATHS } from "constants/constants";
import { IMill } from "../../CorporationsAPIInterfaces";

const CorporationDetails = () => {
    const { corporationId } = useParams();
    const { corporate, millsWithNoDivision, loading, error, refetch } = CorporationsEntity.GetCorporationDetail(
        Number(corporationId),
    );
    const [millData, setMillData] = useState<IMill | null>(null);
    const classes = useStyles();
    const { t } = useTranslation();
    const tableColumns = useCorporationsDetailsColumns(refetch) as unknown as IMainTableColumn[];
    const navigate = useNavigate();

    const corporateDetailData = useMemo(() => {
        const corporateDetailRows = [] as ICorporateDetailRow[];
        if (corporate) {
            const mills = [];
            if (corporate.divisions) {
                corporate.divisions.forEach((division) => {
                    if (division.regions) {
                        division.regions.forEach((region) => {
                            if (region.mills) {
                                region.mills.forEach((mill) => {
                                    mills.push(mill);
                                    corporateDetailRows.push({
                                        id: mill.mill_id,
                                        name: mill.name,
                                        location: mill.physical_address,
                                        division: division.name,
                                        region: region.name,
                                        admin:
                                            mill.users && mill.users?.length > 0
                                                ? `${mill.users?.[0]?.first_name} ${mill.users?.[0]?.last_name}`
                                                : "",
                                        date: mill.created_at,
                                        module: corporate.corporate_module_licenses?.[0]?.module_license?.name,
                                        enabled: mill.is_enabled,
                                        clusterEnabled: mill.cluster_enabled,
                                    });
                                });
                            }
                        });
                    }
                });
            }
            if (millsWithNoDivision.length > 0) {
                millsWithNoDivision.forEach((mill) => {
                    mills.push(mill);
                    corporateDetailRows.push({
                        id: mill.mill_id,
                        name: mill.name,
                        location: mill.physical_address,
                        division: undefined,
                        region: undefined,
                        admin:
                            mill.users && mill.users?.length > 0
                                ? `${mill.users?.[0]?.first_name} ${mill.users?.[0]?.last_name}`
                                : "",
                        date: mill.created_at,
                        module: corporate.corporate_module_licenses?.[0]?.module_license?.name,
                        enabled: mill.is_enabled,
                        clusterEnabled: mill.cluster_enabled,
                    });
                });
            }
        }

        return corporateDetailRows.sort((a, b) => a.id! - b.id!);
    }, [corporate, millsWithNoDivision]);

    const handleOnRowClick = (row: Object) => {
        const millId = (row as ICorporateDetailRow).id;
        corporate?.divisions?.forEach((division) => {
            division.regions?.forEach((region) => {
                region.mills?.forEach((mill) => {
                    if (mill.mill_id === millId) {
                        setMillData(mill);
                    }
                });
            });
        });
        millsWithNoDivision.forEach((mill) => {
            if (mill.mill_id === millId) {
                setMillData(mill);
            }
        });
    };

    return (
        <div className={classes.wrapper}>
            {!millData && (
                <>
                    <div className={classes.title}>
                        <IconButton
                            onClick={() =>
                                navigate(
                                    `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.ROOT}`,
                                )
                            }
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                        {corporate?.name}
                    </div>
                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            className={classes.addButton}
                            onClick={() => setMillData({} as IMill)}
                        >
                            <AddIcon fontSize="small" style={{ marginRight: "5px" }} />
                            {t("setup_corporation.corporation_mills.add_mill_button")}
                        </Button>
                    </div>
                    <div>
                        <MainTable
                            columns={tableColumns}
                            rows={!loading && !error ? corporateDetailData : []}
                            loading={loading}
                            onRowClick={(row: Object) => {
                                handleOnRowClick(row);
                            }}
                            pagination
                        />
                    </div>
                </>
            )}
            {!!millData && (
                <MillForm
                    millData={millData}
                    onCancel={() => setMillData(null)}
                    corporation={corporate && corporate}
                    hideSameAsCorporationAddress
                    closeForm={() => {
                        refetch();
                        setMillData(null);
                    }}
                />
            )}
        </div>
    );
};

export default CorporationDetails;
